import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaShoppingBag, FaRupeeSign } from 'react-icons/fa';
import { AffiliateApiService } from '../../service/affiliateApiService';
import { useLoader } from '../../base/context/LoaderProvider';
import './affiliate.css';

export default function SellerOrderDetails() {
  const { sellerId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const loader = useLoader();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sellerId) {
      setLoading(false);
      setError('Missing seller');
      return;
    }
    loader.showLoading(true);
    AffiliateApiService.getSellerOrders(sellerId)
      .then((res) => {
        loader.showLoading(false);
        setLoading(false);
        if (res.status?.code === 200 && res.data) {
          setData(res.data);
        } else {
          setError(res.error?.message || 'Failed to load');
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        setLoading(false);
        if (err?.status === 403) {
          navigate('/', { replace: true });
          return;
        }
        setError(err?.message || 'Could not load orders');
      });
  }, [sellerId]);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '–');
  const formatDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN') : '–');
  const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  if (loading) {
    return (
      <div className="affiliate-page affiliate-orders-page">
        <p className="affiliate-loading">Loading order details…</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="affiliate-page affiliate-orders-page">
        <h1 className="affiliate-welcome">Order details</h1>
        <p className="affiliate-error">{error}</p>
      </div>
    );
  }

  const orders = data?.orders || [];
  const totalValue = orders.reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="affiliate-page affiliate-orders-page">
      <div className="affiliate-orders-header">
        <h1 className="affiliate-welcome">{data?.seller?.name || 'Seller'} – Order details</h1>
        <p className="affiliate-subtitle">
          {data?.seller?.email && <span className="affiliate-orders-email">{data.seller.email}</span>}
          {data?.orderCount != null && (
            <span className="affiliate-orders-count">
              {' '}· <strong>{data.orderCount}</strong> order(s)
            </span>
          )}
        </p>
      </div>

      {orders.length > 0 && (
        <div className="affiliate-cards" style={{ marginBottom: '1.5rem' }}>
          <div className="affiliate-card">
            <div className="affiliate-card-icon"><FaShoppingBag size={20} /></div>
            <div className="affiliate-card-body">
              <div className="affiliate-card-label">Total orders</div>
              <div className="affiliate-card-value">{orders.length}</div>
            </div>
          </div>
          <div className="affiliate-card">
            <div className="affiliate-card-icon"><FaRupeeSign size={20} /></div>
            <div className="affiliate-card-body">
              <div className="affiliate-card-label">Total value (GMV)</div>
              <div className="affiliate-card-value">{formatMoney(totalValue)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="affiliate-table-wrap affiliate-orders-table-wrap">
        <table className="affiliate-table affiliate-orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Date</th>
              <th>Total</th>
              <th>Status</th>
              <th>Shipped / Delivered</th>
              <th>Tracking</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={6} className="affiliate-orders-empty">
                  No orders yet
                </td>
              </tr>
            ) : (
              orders.map((o) => (
                <tr key={o.upsaleoOrderId || o.orderId || o.createdAt}>
                  <td className="affiliate-orders-id">{o.upsaleoOrderId || o.orderId || '–'}</td>
                  <td>{formatDateTime(o.orderDate || o.createdAt)}</td>
                  <td className="affiliate-orders-total">{formatMoney(o.total)}</td>
                  <td>
                    <span className={`affiliate-badge affiliate-badge-status affiliate-badge-${(o.status || '').replace(/\s/g, '-')}`}>
                      {o.status || '–'}
                    </span>
                  </td>
                  <td>
                    {o.deliveredDate
                      ? `Delivered ${formatDate(o.deliveredDate)}`
                      : o.shipDate
                        ? `Shipped ${formatDate(o.shipDate)}`
                        : o.deliverByDate
                          ? `By ${formatDate(o.deliverByDate)}`
                          : '–'}
                  </td>
                  <td>{o.trackingId || '–'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
